// src/pages/CadastrarUsuario.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { cadastrarUsuario } from "../../api/authService";
import { getUBSs } from "../../api/apiService";
import styles from "../../styles/CadastrarUsuario.module.css";
import {
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { cpf } from "cpf-cnpj-validator";
import ModalAvisoSUSUBS from "../../components/ModalAvisoSUSUBS";

const forbiddenCharacters = /[<>*{};:/\\]/;  // Caracteres perigosos proibidos

const estadosBrasil = [
  { sigla: "AC", nome: "Acre" },
  { sigla: "AL", nome: "Alagoas" },
  { sigla: "AP", nome: "Amapá" },
  { sigla: "AM", nome: "Amazonas" },
  { sigla: "BA", nome: "Bahia" },
  { sigla: "CE", nome: "Ceará" },
  { sigla: "DF", nome: "Distrito Federal" },
  { sigla: "ES", nome: "Espírito Santo" },
  { sigla: "GO", nome: "Goiás" },
  { sigla: "MA", nome: "Maranhão" },
  { sigla: "MT", nome: "Mato Grosso" },
  { sigla: "MS", nome: "Mato Grosso do Sul" },
  { sigla: "MG", nome: "Minas Gerais" },
  { sigla: "PA", nome: "Pará" },
  { sigla: "PB", nome: "Paraíba" },
  { sigla: "PR", nome: "Paraná" },
  { sigla: "PE", nome: "Pernambuco" },
  { sigla: "PI", nome: "Piauí" },
  { sigla: "RJ", nome: "Rio de Janeiro" },
  { sigla: "RN", nome: "Rio Grande do Norte" },
  { sigla: "RS", nome: "Rio Grande do Sul" },
  { sigla: "RO", nome: "Rondônia" },
  { sigla: "RR", nome: "Roraima" },
  { sigla: "SC", nome: "Santa Catarina" },
  { sigla: "SP", nome: "São Paulo" },
  { sigla: "SE", nome: "Sergipe" },
  { sigla: "TO", nome: "Tocantins" },
];

const CadastrarUsuario = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [ubsList, setUbsList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);

  const [formData, setFormData] = useState({
    nomeCompleto: "",
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    cpf: "",
    dataNascimento: "",
    telefone: "",
    cep: "",
    endereco: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    sus: "",
    ubsId: "",
  });
  const [error, setError] = useState({});
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [showPasswordReminder, setShowPasswordReminder] = useState(false);
  const [showUsernameInfo, setShowUsernameInfo] = useState(false);

  useEffect(() => {
    if (location.state) {
      setFormData((prevData) => ({
        ...prevData,
        nomeCompleto: location.state.nomeCompleto || "",
        email: location.state.email || "",
      }));
    }
  }, [location.state]);

  useEffect(() => {
    const fetchUBSs = async () => {
      try {
        const ubsData = await getUBSs();
        setUbsList(ubsData);
      } catch (error) {
        setError((prevErrors) => ({
          ...prevErrors,
          ubsId: "Erro ao carregar UBSs disponíveis.",
        }));
      }
    };

    fetchUBSs();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    // Atualiza o estado do formulário
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    let isPasswordValid = true;

    // Verificação da senha mínima de 8 caracteres
    if (name === "password") {
      if (value.length < 8) {
        isPasswordValid = false;
        setError((prevErrors) => ({
          ...prevErrors,
          password: "A senha deve ter pelo menos 8 caracteres.",
        }));
      } else {
        setError((prevErrors) => ({
          ...prevErrors,
          password: "",
        }));
      }
    }

    if (isPasswordValid) {
      const passwordMatch =
        (name === "password" && value === formData.confirmPassword) ||
        (name === "confirmPassword" && value === formData.password);

      setPasswordMatch(passwordMatch);
      setShowPasswordReminder(passwordMatch && formData.password.length >= 8); // Mostra o lembrete se as senhas coincidem e são válidas

      setError((prevErrors) => ({
        ...prevErrors,
        confirmPassword: passwordMatch ? "" : "As senhas não correspondem.",
      }));
    } else {
      setPasswordMatch(false);
      setShowPasswordReminder(false); // Esconde o lembrete se a senha principal é inválida
    }
  };

  const handleCepChange = async (e) => {
    const value = e.target.value.replace(/\D/g, "");
    const formattedCep =
      value.length <= 5 ? value : `${value.slice(0, 5)}-${value.slice(5, 8)}`;
    setFormData((prevData) => ({ ...prevData, cep: formattedCep }));

    if (value.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${value}/json/`);
        const data = await response.json();

        if (data.erro) {
          setError((prevErrors) => ({
            ...prevErrors,
            cep: "CEP não encontrado.",
          }));
          setFormData((prevData) => ({
            ...prevData,
            endereco: "",
            bairro: "",
            cidade: "",
            estado: "",
          }));
        } else {
          setError((prevErrors) => ({
            ...prevErrors,
            cep: "",
          }));
          setFormData((prevData) => ({
            ...prevData,
            endereco: data.logradouro || "",
            bairro: data.bairro || "",
            cidade: data.localidade || "",
            estado: data.uf || "",
          }));
        }
      } catch (err) {
        setError((prevErrors) => ({
          ...prevErrors,
          cep: "Erro ao buscar informações do CEP.",
        }));
      }
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;

    if (forbiddenCharacters.test(value)) {
      setError((prevErrors) => ({
        ...prevErrors,
        username: "O nome de usuário contém caracteres proibidos." 
      }));
      return; // Impede que o estado seja atualizado com valor inválido
    }

    // Expressão regular para validar o nome de usuário
    const isValid = /^[a-zA-Z0-9._]+$/.test(value);

    if (!isValid) {
      setError((prevErrors) => ({
        ...prevErrors,
        username:
          "O nome de usuário deve conter apenas letras, números, pontos ou underlines, sem espaços.",
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        username: "",
      }));
    }

    // Atualiza o estado apenas com valores válidos
    setFormData((prevData) => ({
      ...prevData,
      username: value.replace(/\s+/g, ""), // Remove quaisquer espaços que o usuário tente inserir
    }));

    if (value.trim() !== "") {
      setShowUsernameInfo(true);
    } else {
      setShowUsernameInfo(false);
    }
  };

  const handleCpfChange = (e) => {
    const rawValue = e.target.value.replace(/\D/g, ""); // Remove tudo que não for número

    if (rawValue.length > 11) return; // Impede que o usuário digite mais de 11 números

    const formattedCpf = cpf.format(rawValue); // Formata automaticamente: "123.456.789-09"

    setFormData((prevData) => ({ ...prevData, cpf: formattedCpf }));

    // Validação e erro
    if (rawValue.length === 11 && !cpf.isValid(rawValue)) {
      setError((prevErrors) => ({
        ...prevErrors,
        cpf: "CPF inválido!",
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        cpf: "",
      }));
    }
  };

  const handleDataNascimentoChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    let formattedDate = "";

    if (value.length <= 2) formattedDate = value;
    else if (value.length <= 4)
      formattedDate = `${value.slice(0, 2)}/${value.slice(2)}`;
    else
      formattedDate = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(
        4,
        8
      )}`;

    if (formattedDate.length === 10) {
      const [dia, mes, ano] = formattedDate.split("/").map(Number);

      const dataValida =
        dia > 0 &&
        dia <= 31 &&
        mes > 0 &&
        mes <= 12 &&
        ano >= 1900 &&
        ano <= new Date().getFullYear();

      const data = new Date(ano, mes - 1, dia);
      const dataFormatada = `${String(data.getDate()).padStart(
        2,
        "0"
      )}/${String(data.getMonth() + 1).padStart(2, "0")}/${data.getFullYear()}`;

      if (!dataValida || formattedDate !== dataFormatada) {
        setError((prevErrors) => ({
          ...prevErrors,
          dataNascimento: "Data de nascimento inválida.",
        }));
        return;
      } else {
        setError((prevErrors) => ({
          ...prevErrors,
          dataNascimento: "",
        }));
      }
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        dataNascimento: "A data deve estar completa no formato DD/MM/AAAA.",
      }));
    }

    setFormData((prevData) => ({ ...prevData, dataNascimento: formattedDate }));
  };

  const handleTelefoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    let formattedTelefone = "";
    if (value.length <= 2) formattedTelefone = `(${value}`;
    else if (value.length <= 6)
      formattedTelefone = `(${value.slice(0, 2)}) ${value.slice(2)}`;
    else if (value.length <= 10)
      formattedTelefone = `(${value.slice(0, 2)}) ${value.slice(
        2,
        6
      )}-${value.slice(6)}`;
    else
      formattedTelefone = `(${value.slice(0, 2)}) ${value.slice(
        2,
        7
      )}-${value.slice(7, 11)}`;
    setFormData((prevData) => ({ ...prevData, telefone: formattedTelefone }));
  };

  const popularDomains = [
    "gmail.com",
    "yahoo.com",
    "hotmail.com",
    "outlook.com",
    "live.com",
    "icloud.com",
    "aol.com",
    "msn.com",
    "yahoo.com.br",
    "uol.com.br",
    "bol.com.br",
    "terra.com.br",
    "ig.com.br",
    "r7.com",
    "zipmail.com.br",
    "globo.com",
    "globomail.com",
    "oi.com.br",
    "hotmail.com.br",
    "outlook.com.br",
  ];

  function getSuggestedDomain(inputDomain) {
    let suggestion = "";
    let minDistance = Infinity;

    popularDomains.forEach((domain) => {
      const distance = levenshteinDistance(inputDomain, domain);
      if (distance < minDistance) {
        minDistance = distance;
        suggestion = domain;
      }
    });

    return minDistance <= 2 ? suggestion : "";
  }

  function levenshteinDistance(a, b) {
    const matrix = [];

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }

  const handleEmailChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z0-9@._-]/g, "");
    setFormData((prevData) => ({ ...prevData, email: value }));

    const emailParts = value.split("@");
    if (emailParts.length === 2 && emailParts[1].length > 0) {
      const suggestion = getSuggestedDomain(emailParts[1]);

      if (suggestion && suggestion !== emailParts[1]) {
        setError((prevErrors) => ({
          ...prevErrors,
          email: `Você quis dizer: ${emailParts[0]}@${suggestion}?`,
        }));
      } else {
        setError((prevErrors) => ({
          ...prevErrors,
          email: "",
        }));
      }
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        email: "",
      }));
    }
  };

  const handleSusChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove todos os caracteres não numéricos
    let formattedSus = "";

    // Formata o SUS em blocos de 3, 4, 4 e 4 dígitos
    if (value.length <= 3) {
      formattedSus = value;
    } else if (value.length <= 7) {
      formattedSus = `${value.slice(0, 3)} ${value.slice(3)}`;
    } else if (value.length <= 11) {
      formattedSus = `${value.slice(0, 3)} ${value.slice(3, 7)} ${value.slice(
        7
      )}`;
    } else {
      formattedSus = `${value.slice(0, 3)} ${value.slice(3, 7)} ${value.slice(
        7,
        11
      )} ${value.slice(11, 15)}`;
    }

    // Valida se o número possui exatamente 15 dígitos
    if (value.length < 15) {
      setError((prevErrors) => ({
        ...prevErrors,
        sus: "O Número do SUS deve conter exatamente 15 dígitos.",
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        sus: "",
      }));
    }

    setFormData((prevData) => ({ ...prevData, sus: formattedSus }));
  };

  const validarFormulario = () => {
    const erros = {};
    if (forbiddenCharacters.test(formData.username)) {
      erros.username = "O nome de usuário contém caracteres proibidos.";
    }
    if (forbiddenCharacters.test(formData.nomeCompleto)) {
      erros.nomeCompleto = "O nome completo contém caracteres proibidos.";
    }
    setError(erros);
    return Object.keys(erros).length === 0; // Retorna true se não houver erros
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      alert("Existem erros no formulário. Corrija-os antes de prosseguir.");
      return;
    }

    const missing = [];
    if (!formData.sus) missing.push("sus");
    if (!formData.ubsId) missing.push("ubs");

    if (missing.length > 0) {
      setMissingFields(missing);
      setOpenModal(true);
      return;
    }

    if (!passwordMatch) {
      setError((prevErrors) => ({
        ...prevErrors,
        confirmPassword: "As senhas não correspondem.",
      }));
      return;
    }

    cadastrarUsuarioFinal();
    try {
      await cadastrarUsuario(formData);
      alert("Cadastro realizado com sucesso!");
      navigate("/");
    } catch (error) {
      if (error.response && error.response.data) {
        // Supondo que o backend retorna um objeto com campo e mensagem
        const backendError = error.response.data;
        setError((prevErrors) => ({
          ...prevErrors,
          [backendError.field]: backendError.message,
        }));
      } else {
        setError((prevErrors) => ({
          ...prevErrors,
          global: "Erro ao cadastrar usuário. Tente novamente.",
        }));
      }
    } finally {
      setIsLoading(false); // Desativar carregamento
    }
  };

  const cadastrarUsuarioFinal = async () => {
    setOpenModal(false);
    setIsLoading(true);
    try {
      await cadastrarUsuario(formData);
      alert("Cadastro realizado com sucesso!");
      navigate("/");
    } catch (error) {
      if (error.response && error.response.data) {
        // Supondo que o backend retorna um objeto com campo e mensagem
        const backendError = error.response.data;
        setError((prevErrors) => ({
          ...prevErrors,
          [backendError.field]: backendError.message,
        }));
      } else {
        setError((prevErrors) => ({
          ...prevErrors,
          global: "Erro ao cadastrar usuário. Tente novamente.",
        }));
      }
    } finally {
      setIsLoading(false); // Desativar carregamento
    }
  };

  const handleNameChange = (e) => {
    const { name, value } = e.target;

    if (forbiddenCharacters.test(value)) {
      setError((prevErrors) => ({
        ...prevErrors,
        nomeCompleto: "O nome completo contém caracteres proibidos."
      }));
      return; // Impede que o nome seja atualizado com valor inválido
    }

    setFormData((prevData) => ({ ...prevData, [name]: value }));

    const words = value.trim().split(/\s+/);
    if (words.length < 2) {
      setError((prevErrors) => ({
        ...prevErrors,
        nomeCompleto: "O nome completo deve ter pelo menos duas palavras.",
      }));
    } else {
      setError((prevErrors) => ({
        ...prevErrors,
        nomeCompleto: "",
      }));
    }
  };

  const handleNameBlur = () => {
    const words = formData.nomeCompleto.trim().split(/\s+/);

    if (words.length >= 2) {
      const formattedName = words
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setFormData((prevData) => ({ ...prevData, nomeCompleto: formattedName }));
    }
  };

  const isFormValid = () => {
    // Verifica se os campos obrigatórios estão preenchidos
    const camposObrigatorios = [
      "nomeCompleto",
      "username",
      "password",
      "confirmPassword",
      "email",
      "cpf",
      "dataNascimento",
      "telefone",
      "cep",
      "endereco",
      "cidade",
      "estado"
    ];
  
    const camposPreenchidos = camposObrigatorios.every(
      (campo) => formData[campo].trim() !== ""
    );
  
    // Verifica se não há erros ativos e se as senhas correspondem
    const semErros = Object.values(error).every((erro) => erro === "");
    
    // Verifica se as senhas coincidem e possuem pelo menos 8 caracteres
    const senhaValida = passwordMatch && formData.password.length >= 8;
  
    return camposPreenchidos && semErros && senhaValida;
  };
  

  return (
    <div className={styles.cadastrarUsuarioContainer}>
      <div className={styles.formCard}>
        <h2>Cadastro de Usuário</h2>
        <p>Preencha os dados abaixo para criar sua conta.</p>
        {error.global && <p className={styles.errorMessage}>{error.global}</p>}

        <form onSubmit={handleSubmit}>
          {/* Dados Pessoais */}
          <fieldset className={styles.fieldset}>
            <legend>Dados Pessoais</legend>
            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                label="Nome Completo"
                name="nomeCompleto"
                value={formData.nomeCompleto}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                required
                error={!!error.nomeCompleto}
                helperText={error.nomeCompleto}
              />
            </div>

            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                label="Data de Nascimento"
                name="dataNascimento"
                value={formData.dataNascimento}
                onChange={handleDataNascimentoChange}
                required
                margin="normal"
                error={!!error.dataNascimento}
                helperText={error.dataNascimento || ""}
              />
              {error.dataNascimento && (
                <p className={styles.errorMessage}>{error.dataNascimento}</p>
              )}
            </div>

            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                label="Telefone"
                name="telefone"
                value={formData.telefone}
                onChange={handleTelefoneChange}
                required
                margin="normal"
              />
            </div>

            <div className={styles.addressGroup}>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="CPF"
                  name="cpf"
                  value={formData.cpf}
                  onChange={handleCpfChange}
                  required
                  margin="normal"
                  error={!!error.cpf}
                  helperText={error.cpf || ""}
                />
              </div>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="Número do SUS"
                  name="sus"
                  value={formData.sus}
                  onChange={handleSusChange}
                  margin="normal"
                  error={!!error.sus}
                  helperText={error.sus || ""}
                />
              </div>
            </div>
          </fieldset>

          {/* Endereço */}
          <fieldset className={styles.fieldset}>
            <div className={styles.inputGroup}>
              <FormControl fullWidth>
                <InputLabel id="ubs-label">Unidade Básica de Saúde</InputLabel>
                <Select
                  labelId="ubs-label"
                  name="ubsId"
                  value={formData.ubsId || ""}
                  onChange={handleChange}
                >
                  {ubsList.map((ubs) => (
                    <MenuItem key={ubs.id} value={ubs.id}>
                      {ubs.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <legend>Endereço</legend>
            <div className={styles.addressGroup}>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="CEP"
                  name="cep"
                  value={formData.cep}
                  onChange={handleCepChange}
                  required
                  margin="normal"
                  error={!!error.cep}
                  helperText={error.cep || ""}
                />
                {error.cep && (
                  <p className={styles.errorMessage}>{error.cep}</p>
                )}
              </div>

              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="Endereço"
                  name="endereco"
                  value={formData.endereco}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </div>
              <div className={`${styles.inputGroup} ${styles.small}`}>
                <TextField
                  fullWidth
                  label="Número"
                  name="numero"
                  value={formData.numero}
                  onChange={handleChange}
                  margin="normal"
                />
              </div>
            </div>
            <div className={styles.addressGroup}>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="Complemento"
                  name="complemento"
                  value={formData.complemento}
                  onChange={handleChange}
                  margin="normal"
                />
              </div>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="Bairro"
                  name="bairro"
                  value={formData.bairro}
                  onChange={handleChange}
                  margin="normal"
                />
              </div>
            </div>
            <div className={styles.addressGroup}>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  label="Cidade"
                  name="Cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </div>
              <div className={styles.inputGroup}>
                <TextField
                  fullWidth
                  select
                  label="Estado"
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  required
                  margin="normal"
                >
                  {estadosBrasil.map((estado) => (
                    <MenuItem key={estado.sigla} value={estado.sigla}>
                      {estado.nome}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          </fieldset>

          {/* Dados de Autenticação */}
          <fieldset className={styles.fieldset}>
            <legend>Dados de Autenticação</legend>
            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                label="Nome de Usuário"
                name="username"
                value={formData.username}
                onChange={handleUsernameChange}
                required
                error={!!error.username}
                helperText={error.username || ""}
              />
            </div>

            {showUsernameInfo && (
              <p style={{ color: "blue", marginTop: "5px" }}>
                ℹ️ No login, você pode usar o Nome de Usuário, Email ou CPF,
                combinado da senha.
              </p>
            )}

            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                type="password"
                label="Senha"
                name="password"
                value={formData.password}
                onChange={handlePasswordChange}
                required
                margin="normal"
                error={!!error.password}
                helperText={error.password}
              />
            </div>
            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                type="password"
                label="Confirme a Senha"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handlePasswordChange}
                required
                margin="normal"
                error={!!error.confirmPassword}
                helperText={error.confirmPassword}
              />
              {!passwordMatch && (
                <p className={styles.passwordError}>
                  As senhas não correspondem.
                </p>
              )}
              {showPasswordReminder && (
                <div className={styles.passwordReminder}>
                  <p style={{ color: "blue", marginTop: "10px" }}>
                    ⚠️ Importante: Anote ou decore sua senha! Você precisará
                    dela para entrar no sistema em seguida.
                  </p>
                </div>
              )}
            </div>
            <div className={styles.inputGroup}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleEmailChange}
                required
                disabled={!!location.state?.email}
                error={!!error.email}
                helperText={error.email || ""}
                margin="normal"
              />
            </div>
          </fieldset>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading || !isFormValid()}
          >
            {isLoading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              "Cadastrar"
            )}
          </Button>
        </form>
      </div>
      <ModalAvisoSUSUBS
        open={openModal}
        onClose={() => setOpenModal(false)}
        onContinue={cadastrarUsuarioFinal}
        missingFields={missingFields}
      />
    </div>
  );
};

export default CadastrarUsuario;
